import { type ReactNode, useCallback, useEffect, useState } from "react";
import BulkATWPopupBase from "~/features/AddCartToWishlist/ACTWPopupBase";
import Spinner from "~/common/components/Spinner/Spinner";
import EditWishlistNamePopup from "~/features/wishlist/components/modals/EditWishlistNamePopup";
import { IconType } from "~/common/components/icon/IconType";
import WishlistDropdown from "~/features/wishlist/components/WishlistDropdown";
import { Trans, useTranslation, type WithTranslation } from "react-i18next";
import { WISHLIST_PAGE } from "~/utils/url/staticUrls";
import { Button, Stack } from "~/common/mui";
import classes from "~/features/orders/orderDetails/components/AddToWishlist/BulkAddToWishlistFlow.module.less";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import { selectWishlists } from "~/features/wishlist/redux/wishlistSelectors";
import type { RootState } from "~/app/store";
import { useDispatch, useSelector } from "react-redux";
import { Alert } from "~/common/mui/Alert/Alert";
import { fetchWishlistNames } from "~/features/wishlist/redux/thunks";

type SuccessMsgFunc = (t: WithTranslation["t"], isNewWishlist: boolean, wishlistName: string) => ReactNode;
type ErrorMsgFunc = (t: WithTranslation["t"]) => ReactNode;

export interface BulkAddToWishlistFlowProps {
  onAddToWishlist: (data: { wishlistId: string } | { wishlistName: string }) => Promise<string>;
  onClose: () => void;
  successMessage?: SuccessMsgFunc;
  errorMessage?: ErrorMsgFunc;
  info?: string;
}
type Props = BulkAddToWishlistFlowProps;

enum Step {
  LOADING,
  HAS_WISHLISTS,
  CREATING_WISHLIST,
  SENDING,
  POST_SENDING_SUCCESS,
  POST_SENDING_ERROR,
}

const getInitialState = (isLoaded: boolean, wishlists: unknown[]) => {
  if (!isLoaded) {
    return Step.LOADING;
  }
  return wishlists.length > 0 ? Step.HAS_WISHLISTS : Step.CREATING_WISHLIST;
};

const DEFAULT_ERR_MSG: ErrorMsgFunc = (t) => t("generic.error.ajax");
const DEFAULT_SUCCESS_MSG: SuccessMsgFunc = (t, isNew, name) => {
  return (
    <Trans i18nKey={`addToWishlist.success.${isNew ? "new" : "existing"}`}>
      Product has been added to wish list "<b>{{ name }}</b>"
    </Trans>
  );
};

const BulkAddToWishlistFlow = (props: Props) => {
  const { isLoaded, wishlists } = useWishlistModels();
  const { onAddToWishlist, onClose, info } = props;
  const successMessageFn = props.successMessage || DEFAULT_SUCCESS_MSG;
  const errorMessageFn = props.errorMessage || DEFAULT_ERR_MSG;

  const { t } = useTranslation();
  const [step, setStep] = useState<Step>(getInitialState(isLoaded, wishlists));
  const [selectedWishlistId, setSelectedWishlistId] = useState<string>(wishlists.length > 0 ? wishlists[0].id : "");
  const [addedWishlistName, setAddedWishlistName] = useState<string>();

  const createNewWishlist = useCallback(() => {
    setStep(Step.CREATING_WISHLIST);
    setSelectedWishlistId("");
  }, []);

  useEffect(() => {
    if (step === Step.LOADING && isLoaded) {
      setStep(wishlists.length > 0 ? Step.HAS_WISHLISTS : Step.CREATING_WISHLIST);
      setSelectedWishlistId(wishlists.length > 0 ? wishlists[0].id : "");
    }
  });

  const addToWishlistCb = useCallback(
    async (data: { wishlistId: string } | { wishlistName: string }) => {
      setStep(Step.SENDING);
      try {
        const addedWishlistName = await onAddToWishlist(data);
        setAddedWishlistName(addedWishlistName);
        setStep(Step.POST_SENDING_SUCCESS);
      } catch {
        setStep(Step.POST_SENDING_ERROR);
      }
    },
    [onAddToWishlist],
  );

  return (
    <>
      {(step === Step.LOADING || step === Step.SENDING) && (
        <BulkATWPopupBase onClose={onClose}>
          <Spinner className={"mt-2"} />
        </BulkATWPopupBase>
      )}
      {step === Step.CREATING_WISHLIST && (
        <EditWishlistNamePopup onOk={(wishlistName) => addToWishlistCb({ wishlistName })} onCancel={onClose} />
      )}
      {step === Step.HAS_WISHLISTS && (
        <BulkATWPopupBase
          onClose={onClose}
          classNames={classes.modalContainer}
          title={t("addCartToWishlist.header")}
          footerChildren={
            <Stack gap={1} direction={"row"}>
              <Button variant={"text"} data-test-id={"add-to-wishlist-btn-cancel"} onClick={onClose}>
                {t("addToWishlist.back")}
              </Button>
              <Button
                data-test-id={"add-to-wishlist-btn-add"}
                onClick={() => addToWishlistCb({ wishlistId: selectedWishlistId! })}
                startIcon={<DefaultButtonIcon type={IconType.Bookmark} />}
              >
                {t("addToWishlist.add")}
              </Button>
            </Stack>
          }
        >
          <div className="pt-1">
            <WishlistDropdown
              wishlists={wishlists}
              // @ts-ignore
              onWishlistSelect={setSelectedWishlistId}
              onNewWishlist={createNewWishlist}
              selectedWishlist={selectedWishlistId}
            />
          </div>
          {info && (
            <Alert severity="info" className="pb-30">
              {info}
            </Alert>
          )}
        </BulkATWPopupBase>
      )}
      {step === Step.POST_SENDING_SUCCESS &&
        (() => {
          const isNewWishlist = selectedWishlistId === "";
          const msg = successMessageFn(t, isNewWishlist, addedWishlistName!);
          return (
            <BulkATWPopupBase
              onClose={onClose}
              footerChildren={
                <Stack direction="row" gap={1}>
                  <Button variant={"text"} data-test-id={"add-to-wishlist-btn-cancel"} onClick={onClose}>
                    {t("addToWishlist.back")}
                  </Button>
                  <Button
                    data-test-id={"add-to-wishlist-btn-show"}
                    href={WISHLIST_PAGE}
                    startIcon={<DefaultButtonIcon type={IconType.Bookmark} />}
                  >
                    {t("addToWishlist.show")}
                  </Button>
                </Stack>
              }
            >
              <Alert severity={"info"} className={"mb-30"}>
                {msg}
              </Alert>
            </BulkATWPopupBase>
          );
        })()}
      {step === Step.POST_SENDING_ERROR && (
        <BulkATWPopupBase
          onClose={onClose}
          footerChildren={
            <Stack direction="row" gap={1}>
              <Button variant={"text"} data-test-id={"add-to-wishlist-btn-cancel"} onClick={onClose}>
                {t("addToWishlist.back")}
              </Button>
              <Button
                data-test-id={"add-to-wishlist-btn-show"}
                href={WISHLIST_PAGE}
                startIcon={<DefaultButtonIcon type={IconType.Bookmark} />}
              >
                {t("addToWishlist.show")}
              </Button>
            </Stack>
          }
        >
          <Alert severity={"error"} className={"mb-30"}>
            {errorMessageFn(t)}
          </Alert>
        </BulkATWPopupBase>
      )}
    </>
  );
};

export default BulkAddToWishlistFlow;

function useWishlistModels() {
  const dispatch = useDispatch();
  const wishlists = useSelector(selectWishlists);
  const isLoaded = useSelector((state: RootState) => state.wishlistPage.loaded);
  const isLoading = useSelector((state: RootState) => state.wishlistPage.loading);

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      dispatch(fetchWishlistNames());
    }
  }, [dispatch, isLoaded, isLoading]);

  return {
    wishlists,
    isLoaded: isLoaded && !isLoading,
  };
}
